import React from 'react';
import { Platform, StyleSheet, TextStyle } from 'react-native';
import { Text as RNPText } from 'react-native-paper';

import Icon from '@components/Icon';
import { Row } from '@components/View';
import { useAppTheme } from '@context/ThemeContext';

type TypographyProps = React.ComponentProps<typeof RNPText>;
type TypographyFamily = 'regular' | 'medium';

type LegacyTypographyProps = TypographyProps & {
  alpha: number;
  family: TypographyFamily;
  textStyle: TextStyle;
};

type IconTypographyProps = {
  icon: React.ComponentProps<typeof Icon>['name'];
  text: string;
  style?: React.ComponentProps<typeof Row>['style'];
  iconProps?: Omit<React.ComponentProps<typeof Icon>, 'name'>;
};

function LegacyTypography({ alpha, family, style, textStyle, ...props }: LegacyTypographyProps) {
  const appTheme = useAppTheme();

  return (
    <RNPText
      {...props}
      style={[
        {
          color: getAlphaColor(appTheme.colors.text, alpha),
          ...appTheme.fonts[family],
        },
        textStyle,
        style,
      ]}
    />
  );
}

type HeadlineProps = TypographyProps;

export function Headline(props: HeadlineProps) {
  return <LegacyTypography alpha={0.87} family="regular" textStyle={styles.headline} {...props} />;
}

export function PrimaryHeadline({ style, ...props }: HeadlineProps) {
  const primaryStyle = usePrimaryStyle();
  return <Headline style={[primaryStyle, style]} {...props} />;
}

export function WhiteHeadline({ style, ...props }: HeadlineProps) {
  return <Headline style={[styles.whiteText, style]} {...props} />;
}

export function IconHeadline({ icon, text, style }: IconTypographyProps) {
  return (
    <Row style={style}>
      <Icon name={icon} style={styles.icon} />
      <Headline>{text}</Headline>
    </Row>
  );
}

type TitleProps = TypographyProps;

export function Title(props: TitleProps) {
  return <LegacyTypography alpha={0.87} family="medium" textStyle={styles.title} {...props} />;
}

export function PrimaryTitle({ style, ...props }: TitleProps) {
  const primaryStyle = usePrimaryStyle();
  return <Title style={[primaryStyle, style]} {...props} />;
}

export function WhiteTitle({ style, ...props }: TitleProps) {
  return <Title style={[styles.whiteText, style]} {...props} />;
}

export function IconTitle({ icon, text, style }: IconTypographyProps) {
  return (
    <Row style={style}>
      <Icon name={icon} style={styles.icon} />
      <Title>{text}</Title>
    </Row>
  );
}

type SubheadingProps = TypographyProps;

export function Subheading(props: SubheadingProps) {
  return (
    <LegacyTypography alpha={0.87} family="regular" textStyle={styles.subheading} {...props} />
  );
}

export function IconSubheading({ icon, text, style }: IconTypographyProps) {
  return (
    <Row style={style}>
      <Icon name={icon} style={styles.icon} />
      <Subheading>{text}</Subheading>
    </Row>
  );
}

export function PrimarySubheading({ style, ...props }: SubheadingProps) {
  const primaryStyle = usePrimaryStyle();
  return <Subheading style={[primaryStyle, style]} {...props} />;
}

export function WhiteSubheading({ style, ...props }: SubheadingProps) {
  return <Subheading style={[styles.whiteText, style]} {...props} />;
}

type ParagraphProps = TypographyProps;

export function Paragraph(props: ParagraphProps) {
  return <LegacyTypography alpha={0.87} family="regular" textStyle={styles.paragraph} {...props} />;
}

export function WhiteParagraph({ style, ...props }: ParagraphProps) {
  return <Paragraph style={[styles.whiteText, style]} {...props} />;
}

export function IconParagraph({ icon, text, style }: IconTypographyProps) {
  return (
    <Row style={style}>
      <Icon name={icon} style={styles.icon} />
      <Paragraph>{text}</Paragraph>
    </Row>
  );
}

type CaptionProps = TypographyProps;

export function Caption(props: CaptionProps) {
  return <LegacyTypography alpha={0.54} family="regular" textStyle={styles.caption} {...props} />;
}

export function WhiteCaption({ style, ...props }: CaptionProps) {
  return <Caption style={[styles.whiteText, style]} {...props} />;
}

export function IconCaption({ icon, text, style, iconProps }: IconTypographyProps) {
  return (
    <Row style={style}>
      <Icon name={icon} style={styles.icon} {...iconProps} />
      <Caption>{text}</Caption>
    </Row>
  );
}

export function usePrimaryStyle() {
  const appTheme = useAppTheme();
  const primaryColor = {
    color: appTheme.colors.primary,
    ...styles.primary,
  };
  return primaryColor;
}

function getAlphaColor(color: string, alpha: number) {
  const hexColor = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color);
  if (!hexColor) return color;

  const normalized =
    hexColor[1].length === 3
      ? hexColor[1]
          .split('')
          .map((value) => value + value)
          .join('')
      : hexColor[1];

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

const styles = StyleSheet.create({
  caption: {
    fontSize: 12,
    letterSpacing: 0.4,
    lineHeight: 20,
    marginVertical: 2,
  },
  headline: {
    fontSize: 24,
    letterSpacing: 0,
    lineHeight: 32,
    marginVertical: 2,
  },
  icon: {
    marginRight: 5,
  },
  paragraph: {
    fontSize: 14,
    letterSpacing: 0.25,
    lineHeight: 20,
    marginVertical: 2,
  },
  primary: {
    fontWeight: 'bold',
  },
  subheading: {
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
    marginVertical: 2,
  },
  title: {
    fontSize: 20,
    letterSpacing: 0.15,
    lineHeight: 30,
    marginVertical: 2,
  },
  whiteText: {
    color: 'white',
    fontWeight: 'bold',
    paddingHorizontal: 2,
    ...Platform.select({
      default: {
        textShadowColor: 'black',
        textShadowOffset: {
          width: 0,
          height: 2,
        },
        textShadowRadius: 5,
      },
      web: {
        textShadow: '0px 2px 5px black',
      },
    }),
  },
});
